import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PERMISSION_GROUPS,
  PERMISSION_PRESETS,
  PERMISSION_LABEL_KEY,
  PERMISSION_PAGE_KEY,
  normalizePermissions,
  levelOf,
  applyLevel,
  PERMISSION_LEVELS,
  type AccessLevel,
  type AccessFamily,
  type Permission,
  type MemberRoleInput,
} from '../lib/permissions';
import { previewSidebarFor, describeMember } from '../lib/memberPreview';

/**
 * AccessPanel — the per-member « Access » screen of Band Settings (chantier C,
 * 2026-09-25). It REPLACES the two ad-hoc checkboxes (`finance_access`,
 * `layout_access`) with one checkbox per capability, grouped by the page it
 * unlocks, plus the presets of the brief (Editor, Editor + layouts, Finance
 * only, Crew = layouts + content).
 *
 * Fully DATA-DRIVEN: the 11 capabilities, the 3 groups and the 4 presets all
 * come from `lib/permissions.ts`, so adding a delegable family (the 4
 * `content.*` names of the « delegated content » lot) needed no structural
 * change here — one checkbox per capability, same pattern as today.
 *
 * Rendering follows the project's shared-grid « tracks table » pattern
 * (`.access-matrix` = one grid for the header AND every row, horizontally
 * scrollable on mobile) so it looks like every other list/editor of the app.
 *
 * The panel is STATELESS: the member row owns the granted set, the panel only
 * computes the next COMPLETE set and hands it to `onSave` (the server replaces
 * the whole matrix — rows absent from the array are deleted). A checkbox click
 * therefore saves immediately, exactly like the two checkboxes it replaces.
 */
export interface AccessPanelProps {
  /** The member row (role + granted capabilities). */
  member: MemberRoleInput;
  /** A write is in flight for THIS member: every control is disabled. */
  saving?: boolean;
  /** Persist the complete state of this member: grants AND explicit read denies. */
  onSave: (permissions: Permission[], denied: string[]) => void | Promise<void>;
}

export default function AccessPanel({ member, saving = false, onSave }: AccessPanelProps) {
  const { t } = useTranslation();
  const granted = normalizePermissions(member.permissions);
  // An owner always holds every capability server-side (and a superadmin maps
  // to owner): the panel says so instead of offering checkboxes that could not
  // be revoked.
  const isOwner = member.role === 'owner';
  // Preview open/closed. Declared ABOVE the early return below: a hook after a
  // conditional return breaks the hook order (React #310, blank screen).
  const [previewOpen, setPreviewOpen] = useState(false);

  // ── The page×level grid (2026-10-08) ────────────────────────────────────────
  // A checkbox could only say « has the right / does not » and made a family the
  // member reads-but-cannot-write look identical to a family he cannot open at
  // all. Each family now carries a LEVEL, and `applyLevel` computes the complete
  // new state (grants + denies) — the panel never assembles a partial set itself.
  const setLevel = (permission: AccessFamily, level: AccessLevel) => {
    const next = applyLevel(member, permission, level);
    void onSave(next.permissions, next.denied);
  };

  /** The levels this family can actually reach, with the reason when one is out. */
  const levelsFor = (permission: AccessFamily) => {
    const spec = PERMISSION_LEVELS[permission];
    return {
      view: !!spec?.viewName || !!spec?.openReads,
      viewTitleKey: !spec?.viewName && !spec?.openReads
        ? 'access.levelViewUnavailable'
        : (spec?.openReads && !spec?.viewName ? 'access.levelViewDefault' : 'access.levelViewTitle'),
      noneTitleKey: spec?.denyName ? 'access.levelNoneTitle' : 'access.levelNoneNoReadTitle',
      // Une PAGE déniable (`venues`, …) n'a pas d'écriture délégable : ✏️ est
      // désactivé AVEC sa raison plutôt que d'offrir un octroi impossible.
      edit: spec?.grantable !== false,
      editTitleKey: spec?.grantable === false ? 'access.levelEditUnavailable' : 'access.levelEditTitle',
    };
  };

  if (isOwner) {
    return <p className="text-dim" style={{ margin: 0 }}>{t('access.always')}</p>;
  }

  return (
    <div className="access-panel">
      <div className="access-matrix-wrap">
        <div className="access-matrix">
          <span className="access-head">{t('access.columnCapability')}</span>
          <span className="access-head" style={{ gridColumn: '2 / -1' }}>{t('access.columnLevel')}</span>
          {PERMISSION_GROUPS.map((group) => (
            <Fragment key={group.id}>
              <span className="access-group">{t(group.labelKey)}</span>
              {group.permissions.map((permission) => {
                const level = levelOf(member, permission);
                const caps = levelsFor(permission);
                const label = t(PERMISSION_LABEL_KEY[permission]);
                const LEVELS: { id: AccessLevel; glyph: string; titleKey: string; enabled: boolean }[] = [
                  { id: 'none', glyph: '—', titleKey: caps.noneTitleKey, enabled: true },
                  { id: 'view', glyph: '👁', titleKey: caps.viewTitleKey, enabled: caps.view },
                  { id: 'edit', glyph: '✏️', titleKey: caps.editTitleKey, enabled: caps.edit },
                ];
                return (
                  <Fragment key={permission}>
                    <span className="access-levels" role="radiogroup" data-family={permission} aria-label={label}>
                      {LEVELS.map((lv) => (
                        <button
                          key={lv.id}
                          type="button"
                          className={`access-level${level === lv.id ? ' access-level-on' : ''}`}
                          disabled={saving || !lv.enabled}
                          aria-pressed={level === lv.id}
                          data-level={lv.id}
                          title={`${t(lv.titleKey)} — ${label}`}
                          onClick={() => setLevel(permission, lv.id)}
                        >
                          {lv.glyph}
                        </button>
                      ))}
                    </span>
                    <label
                      className="access-label"
                      title={t(PERMISSION_LABEL_KEY[permission])}
                    >
                      {t(PERMISSION_LABEL_KEY[permission])}
                    </label>
                    <span className="text-dim access-applies" style={{ fontSize: 'var(--font-xs)' }}>
                      {t(PERMISSION_PAGE_KEY[permission])}
                    </span>
                  </Fragment>
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>

      <div className="access-presets">
        <span className="text-dim" style={{ fontSize: 'var(--font-sm)' }}>{t('access.presetHint')}</span>
        {PERMISSION_PRESETS.map((preset) => {
          const active = preset.permissions.length === granted.length
            && preset.permissions.every((p) => granted.includes(p));
          return (
            <button
              key={preset.id}
              type="button"
              className="btn btn-secondary btn-small"
              disabled={saving}
              aria-pressed={active}
              onClick={() => void onSave([...preset.permissions], [])}
            >
              {t(preset.labelKey)}
            </button>
          );
        })}
      </div>
      {/* ── Preview: what does THIS member see? ──────────────────────────────
          The feature that was missing: until v0.659 the only way to know was to
          log in as the member — which is exactly how the Seb case (a `tech`
          holding the rider rights, unable to see the config pages) stayed hidden.
          Computed by `previewSidebarFor` with the SAME predicates as the sidebar. */}
      <div className="access-preview" style={{ marginTop: 8 }}>
        <button
          type="button"
          className="btn btn-secondary btn-small"
          aria-expanded={previewOpen}
          data-preview="1"
          onClick={() => setPreviewOpen((v) => !v)}
        >
          👁 {t('access.previewTitle')}
        </button>
        <p className="text-dim" style={{ fontSize: 'var(--font-xs)', margin: '4px 0 0' }}>
          {describeMember(member, t)}
        </p>
        {previewOpen && (
          <div style={{ marginTop: 6 }}>
            <p className="text-dim" style={{ fontSize: 'var(--font-xs)', margin: '0 0 6px' }}>
              {t('access.previewHint')}
            </p>
            {previewSidebarFor(member).map((section) => (
              <div key={section.id} style={{ marginBottom: 4 }}>
                <strong style={{ fontSize: 'var(--font-sm)' }}>
                  {section.visible ? '✅' : '—'} {t(section.labelKey)}
                </strong>
                <ul style={{ margin: '2px 0 0 16px', padding: 0, listStyle: 'none' }}>
                  {section.items.map((it) => (
                    <li
                      key={it.id}
                      className={it.visible ? '' : 'text-dim'}
                      style={{ fontSize: 'var(--font-xs)' }}
                    >
                      {it.visible ? '· ' : '· '}
                      {t(it.labelKey)}
                      {it.visible && (
                        <span className="text-dim"> — {t('access.previewYes')}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}